import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, addDoc, doc, updateDoc, getDoc } from 'firebase/firestore';
import Header from '../components/Header';
import Footer from '../components/Footer';
import DataProtectionNotice from '../components/DataProtectionNotice';
import { runScheduleCheck, getScheduleIntervalMs } from '../utils/scheduleCheck';
import './LoginPage.css';

const LoginPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [activeCompetition, setActiveCompetition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    checkActiveCompetition();
    runScheduleCheck();
    const scheduleId = setInterval(runScheduleCheck, getScheduleIntervalMs());
    return () => clearInterval(scheduleId);
  }, []);

  const checkActiveCompetition = async () => {
    try {
      const competitionsRef = collection(db, 'competitions');
      const q = query(competitionsRef, where('isActive', '==', true));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const competition = querySnapshot.docs[0].data();
        competition.id = querySnapshot.docs[0].id;
        setActiveCompetition(competition);
      }
    } catch (err) {
      console.error('Error checking competition:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim() || !dateOfBirth || !gender) {
      setError(t('fillAllFields'));
      return;
    }

    const birthDate = new Date(dateOfBirth);
    if (birthDate.getTime() > Date.now()) {
      setError(t('dateCannotBeInFuture'));
      return;
    }

    if (!activeCompetition) {
      setError(t('noActiveCompetition'));
      return;
    }

    // Validate age restrictions
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'global'));
      if (settingsDoc.exists()) {
        const settings = settingsDoc.data();
        const minAge = settings.minAge || 0;
        const maxAge = settings.maxAge || 120;
        
        // Calculate age from date of birth
        const birthDate = new Date(dateOfBirth);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        
        if (age < minAge || age > maxAge) {
          const errorMsg = t('ageRestrictionError')
            .replace('{minAge}', minAge)
            .replace('{maxAge}', maxAge);
          setError(errorMsg);
          return;
        }
      }
    } catch (err) {
      console.error('Error checking age settings:', err);
      // Continue if age check fails (don't block login)
    }

    try {
      // Check if user already exists in this competition
      const usersRef = collection(db, 'competitions', activeCompetition.id, 'users');
      const q = query(
        usersRef,
        where('name', '==', name.trim()),
        where('dateOfBirth', '==', dateOfBirth)
      );
      const existingUsers = await getDocs(q);

      let userId;
      if (!existingUsers.empty) {
        // User exists, get their ID
        userId = existingUsers.docs[0].id;
        const userData = existingUsers.docs[0].data();
        
        // Check if user is frozen
        if (userData.frozen) {
          setError(t('accountFrozen'));
          return;
        }

        // Update last login and gender (gender can be changed on re-authentication)
        await updateDoc(doc(db, 'competitions', activeCompetition.id, 'users', userId), {
          lastLogin: new Date(),
          gender: gender
        });
      } else {
        // Create new user
        const newUser = {
          name: name.trim(),
          dateOfBirth,
          gender: gender,
          score: 0,
          currentRound: 0,
          frozen: false,
          joinedAt: new Date(),
          lastLogin: new Date(),
          answers: [],
          roundStartTime: null,
          roundTimeLeft: null
        };
        const docRef = await addDoc(usersRef, newUser);
        userId = docRef.id;
      }

      // Store user session
      localStorage.setItem('userId', userId);
      localStorage.setItem('competitionId', activeCompetition.id);
      localStorage.setItem('userName', name.trim());
      localStorage.setItem('userDateOfBirth', dateOfBirth);

      navigate('/user');
    } catch (err) {
      console.error('Error logging in:', err);
      setError(t('errorOccurred'));
    }
  };

  if (loading) {
    return (
      <div className="login-page">
        <Header />
        <div className="loading">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <Header />
      <div className="login-container">
        <h1>{t('joinCompetition')}</h1>
        
        {!activeCompetition ? (
          <div className="no-competition">
            <p>{t('noActiveCompetition')}</p>
          </div>
        ) : (
          <>
            <h2>{activeCompetition.name}</h2>
            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="name">{t('name')} <span className="required-indicator">*</span></label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="dateOfBirth">{t('dateOfBirth')} <span className="required-indicator">*</span></label>
                <input
                  type="date"
                  id="dateOfBirth"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="gender">{t('gender')} <span className="required-indicator">*</span></label>
                <select
                  id="gender"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  required
                >
                  <option value="">{t('selectGender')}</option>
                  <option value="male">{t('male')}</option>
                  <option value="female">{t('female')}</option>
                </select>
              </div>

              {error && <div className="error">{error}</div>}

              <button type="submit" className="submit-button">
                {t('joinCompetition')}
              </button>
            </form>
          </>
        )}

        <DataProtectionNotice />
      </div>
      <Footer />
    </div>
  );
};

export default LoginPage;

